/**
 * Imperative document <head> updates for core SEO tags.
 * Avoids duplicate nodes (static index.html + react-helmet-async) by updating
 * or deduping a single element per logical tag.
 */

const PAGINATION_PREV_ATTR = "data-apt-seo-pagination-prev";
const PAGINATION_NEXT_ATTR = "data-apt-seo-pagination-next";

export interface DocumentHeadSeoInput {
  title: string;
  description: string;
  canonicalHref: string;
  keywords: string;
  author: string;
  robots: string;
  googlebot: string;
  ogType: string;
  ogTitle: string;
  ogDescription: string;
  ogImage: string;
  ogImageWidth?: number | null;
  ogImageHeight?: number | null;
  skipSocialImage?: boolean;
  ogSiteName: string;
  ogLocale: string;
  twitterCard: string;
  twitterTitle: string;
  twitterDescription: string;
  twitterImage: string;
  twitterSite: string;
  linkPrev?: string | null;
  linkNext?: string | null;
  googleSiteVerification?: string | null;
}

function ensureSingleMetaByName(name: string): HTMLMetaElement {
  const all = document.head.querySelectorAll<HTMLMetaElement>(
    `meta[name="${name}"]`,
  );
  if (all.length === 0) {
    const el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
    return el;
  }
  const keep = all[0];
  for (let i = 1; i < all.length; i++) {
    all[i].remove();
  }
  return keep;
}

function ensureSingleMetaByProperty(property: string): HTMLMetaElement {
  const all = document.head.querySelectorAll<HTMLMetaElement>(
    `meta[property="${property}"]`,
  );
  if (all.length === 0) {
    const el = document.createElement("meta");
    el.setAttribute("property", property);
    document.head.appendChild(el);
    return el;
  }
  const keep = all[0];
  for (let i = 1; i < all.length; i++) {
    all[i].remove();
  }
  return keep;
}

function syncDocumentTitle(text: string): void {
  document.title = text;
  const titles = document.head.querySelectorAll("title");
  titles.forEach((node, index) => {
    if (index === 0) {
      node.textContent = text;
    } else {
      node.remove();
    }
  });
}

function setCanonicalHref(href: string): void {
  const links = document.head.querySelectorAll<HTMLLinkElement>(
    'link[rel="canonical"]',
  );
  if (links.length === 0) {
    const link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    link.setAttribute("href", href);
    document.head.appendChild(link);
    return;
  }
  links[0].setAttribute("href", href);
  for (let i = 1; i < links.length; i++) {
    links[i].remove();
  }
}

function removeManagedPaginationLinks(): void {
  document.head
    .querySelectorAll(
      `link[${PAGINATION_PREV_ATTR}], link[${PAGINATION_NEXT_ATTR}]`,
    )
    .forEach((el) => el.remove());
}

function syncPaginationLinks(
  linkPrev?: string | null,
  linkNext?: string | null,
): void {
  removeManagedPaginationLinks();
  if (linkPrev) {
    const link = document.createElement("link");
    link.setAttribute("rel", "prev");
    link.setAttribute("href", linkPrev);
    link.setAttribute(PAGINATION_PREV_ATTR, "true");
    document.head.appendChild(link);
  }
  if (linkNext) {
    const link = document.createElement("link");
    link.setAttribute("rel", "next");
    link.setAttribute("href", linkNext);
    link.setAttribute(PAGINATION_NEXT_ATTR, "true");
    document.head.appendChild(link);
  }
}

/**
 * Apply route SEO to the live document head (single node per tag).
 * Safe to call on every navigation from SEOHead via useLayoutEffect.
 */
export function applyDocumentHeadSeo(input: DocumentHeadSeoInput): void {
  if (typeof document === "undefined" || !document.head) {
    return;
  }

  syncDocumentTitle(input.title);

  ensureSingleMetaByName("description").setAttribute(
    "content",
    input.description,
  );
  ensureSingleMetaByName("keywords").setAttribute("content", input.keywords);
  ensureSingleMetaByName("author").setAttribute("content", input.author);
  ensureSingleMetaByName("robots").setAttribute("content", input.robots);
  ensureSingleMetaByName("googlebot").setAttribute("content", input.googlebot);

  if (input.googleSiteVerification) {
    ensureSingleMetaByName("google-site-verification").setAttribute(
      "content",
      input.googleSiteVerification,
    );
  }

  setCanonicalHref(input.canonicalHref);
  syncPaginationLinks(input.linkPrev, input.linkNext);

  ensureSingleMetaByProperty("og:type").setAttribute("content", input.ogType);
  ensureSingleMetaByProperty("og:url").setAttribute(
    "content",
    input.canonicalHref,
  );
  ensureSingleMetaByProperty("og:title").setAttribute("content", input.ogTitle);
  ensureSingleMetaByProperty("og:description").setAttribute(
    "content",
    input.ogDescription,
  );
  if (!input.skipSocialImage) {
    const ogImageEl = ensureSingleMetaByProperty("og:image");
    ogImageEl.setAttribute("content", input.ogImage);
    ogImageEl.removeAttribute("data-apt-social");
    if (input.ogImageWidth != null && input.ogImageHeight != null) {
      ensureSingleMetaByProperty("og:image:width").setAttribute(
        "content",
        String(input.ogImageWidth),
      );
      ensureSingleMetaByProperty("og:image:height").setAttribute(
        "content",
        String(input.ogImageHeight),
      );
    } else {
      document.head
        .querySelectorAll(
          'meta[property="og:image:width"], meta[property="og:image:height"]',
        )
        .forEach((el) => el.remove());
    }
    const twitterCardEl = ensureSingleMetaByProperty("twitter:card");
    twitterCardEl.setAttribute("content", input.twitterCard);
    twitterCardEl.removeAttribute("data-apt-social");
    const twitterImageEl = ensureSingleMetaByProperty("twitter:image");
    twitterImageEl.setAttribute("content", input.twitterImage);
    twitterImageEl.removeAttribute("data-apt-social");
  }
  ensureSingleMetaByProperty("og:site_name").setAttribute(
    "content",
    input.ogSiteName,
  );
  ensureSingleMetaByProperty("og:locale").setAttribute(
    "content",
    input.ogLocale,
  );
  ensureSingleMetaByProperty("twitter:url").setAttribute(
    "content",
    input.canonicalHref,
  );
  ensureSingleMetaByProperty("twitter:title").setAttribute(
    "content",
    input.twitterTitle,
  );
  document.head
    .querySelectorAll('meta[property="twitter:description"]')
    .forEach((el) => el.remove());
  ensureSingleMetaByName("twitter:description").setAttribute(
    "content",
    input.twitterDescription,
  );
  ensureSingleMetaByProperty("twitter:site").setAttribute(
    "content",
    input.twitterSite,
  );

  // PWA / tile colors: shell provides defaults; collapse extras if another Helmet adds duplicates.
  ensureSingleMetaByName("theme-color").setAttribute("content", "#1976d2");
  ensureSingleMetaByName("msapplication-TileColor").setAttribute(
    "content",
    "#1976d2",
  );
}
